import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from 'prosemirror-state'
import { Decoration, DecorationSet } from 'prosemirror-view'
import type { Node as PMNode } from 'prosemirror-model'

export interface CollapsedContentOptions {
    enabled: boolean
    maxChars: number
    /**
     * Max number of visible rendered "lines" (roughly: paragraphs + hard breaks).
     * When exceeded, content after the Nth line is visually hidden.
     *
     * Set to <= 0 (or non-finite) to disable line-based collapsing.
     */
    maxLines?: number
    instanceId?: string
    buttonId?: string
    /**
     * If true, shows a clickable "more" control after the ellipsis.
     * When false, collapsed content still shows the ellipsis without an action.
     */
    showMoreControl?: boolean
}

export const collapsedContentPluginKey = new PluginKey('knovCollapsedContent')

type TextblockInfo = { node: PMNode; pos: number }

function isCollapseDebugEnabled(): boolean {
    try {
        // @ts-expect-error - __KNOV_COLLAPSE_DEBUG is a custom debug flag set at runtime
        return typeof window !== 'undefined' && window.__KNOV_COLLAPSE_DEBUG === true
    } catch {
        return false
    }
}

const lastLogAtByInstance = new Map<string, number>()

function collapseLog(instanceId: string | undefined, msg: string, data?: Record<string, unknown>) {
    if (!isCollapseDebugEnabled()) return

    const key = instanceId || 'unknown'
    const now = Date.now()
    const lastAt = lastLogAtByInstance.get(key) || 0
    // Rate-limit per instance to avoid console spam
    if (now - lastAt < 500) return
    lastLogAtByInstance.set(key, now)

    try {
        // eslint-disable-next-line no-console
        console.log(`[CollapsedContent][${key}] ${msg}`, data || {})
    } catch {}
}

function collectTextblocks(doc: PMNode): TextblockInfo[] {
    const blocks: TextblockInfo[] = []
    doc.descendants((node: PMNode, pos: number) => {
        if (node?.isTextblock) {
            blocks.push({ node, pos })
        }
    })
    return blocks
}

function getTotalTextLength(doc: PMNode): number {
    // Rough parity with “plain text” length: include newline separators between textblocks.
    const blocks = collectTextblocks(doc)
    if (!blocks.length) return 0

    let total = 0
    for (let i = 0; i < blocks.length; i++) {
        const b = blocks[i]
        const text = b.node.textBetween(0, b.node.content.size, '\n', '\n') || ''
        total += text.length
        if (i < blocks.length - 1) total += 1 // newline between blocks
    }
    return total
}

function getTotalLineCount(doc: PMNode): number {
    const blocks = collectTextblocks(doc)
    if (!blocks.length) return 0

    let totalLines = 0

    for (const b of blocks) {
        // A textblock is at least one visual "line" even if empty
        let blockLines = 1

        // Count explicit hard breaks inside the block as additional lines
        b.node.nodesBetween(0, b.node.content.size, (n: PMNode) => {
            if (n?.type?.name === 'hardBreak') blockLines += 1
            return true
        })

        totalLines += blockLines
    }

    return totalLines
}

function findCutPosByLines(
    doc: PMNode,
    maxLines: number,
): { cutPos: number | null; cutBlockEnd: number | null } {
    if (!doc) return { cutPos: null, cutBlockEnd: null }
    if (!Number.isFinite(maxLines) || maxLines <= 0) return { cutPos: null, cutBlockEnd: null }

    const blocks = collectTextblocks(doc)
    if (!blocks.length) return { cutPos: null, cutBlockEnd: null }

    let linesSeen = 0

    for (let i = 0; i < blocks.length; i++) {
        const { node: blockNode, pos: blockPos } = blocks[i]

        // If we already reached the cap before this block, cut at the end of the previous block
        if (linesSeen >= maxLines) {
            const prev = blocks[i - 1]
            if (!prev) return { cutPos: null, cutBlockEnd: null }
            const prevEnd = prev.pos + prev.node.nodeSize - 1
            return { cutPos: prevEnd, cutBlockEnd: prevEnd }
        }

        // Within this block, hardBreaks create new lines. If the next hardBreak would
        // exceed maxLines, cut right before that hardBreak.
        let currentLineInBlock = 1
        let cutRelativePos: number | null = null

        blockNode.nodesBetween(0, blockNode.content.size, (n: PMNode, p: number) => {
            if (cutRelativePos != null) return false

            if (n?.type?.name === 'hardBreak') {
                // If we're already on the maxLines-th line, starting a new line would exceed.
                if (linesSeen + currentLineInBlock >= maxLines) {
                    cutRelativePos = p
                    return false
                }
                currentLineInBlock += 1
            }

            return true
        })

        const cutBlockEnd = blockPos + blockNode.nodeSize - 1

        if (cutRelativePos != null) {
            const cutPos = blockPos + 1 + cutRelativePos
            return { cutPos, cutBlockEnd }
        }

        // No internal cut. If we've hit the line cap at the end of this block and there are
        // more blocks, cut at this block's end so the next block is hidden.
        if (linesSeen + currentLineInBlock >= maxLines) {
            if (i < blocks.length - 1) {
                const cutPos = cutBlockEnd
                return { cutPos, cutBlockEnd: cutPos }
            }
        }

        linesSeen += currentLineInBlock
    }

    return { cutPos: null, cutBlockEnd: null }
}

function chooseEarlierCut(
    a: { cutPos: number | null; cutBlockEnd: number | null },
    b: { cutPos: number | null; cutBlockEnd: number | null },
): { cutPos: number | null; cutBlockEnd: number | null } {
    if (a.cutPos == null || a.cutBlockEnd == null) return b
    if (b.cutPos == null || b.cutBlockEnd == null) return a
    return a.cutPos <= b.cutPos ? a : b
}

function findCutPos(
    doc: PMNode,
    maxChars: number,
): { cutPos: number | null; cutBlockEnd: number | null } {
    if (!doc) return { cutPos: null, cutBlockEnd: null }
    if (!Number.isFinite(maxChars) || maxChars <= 0) return { cutPos: null, cutBlockEnd: null }

    const blocks = collectTextblocks(doc)
    if (!blocks.length) return { cutPos: null, cutBlockEnd: null }

    let count = 0

    for (let i = 0; i < blocks.length; i++) {
        const { node: blockNode, pos: blockPos } = blocks[i]
        const blockText = blockNode.textBetween(0, blockNode.content.size, '\n', '\n') || ''
        const blockLen = blockText.length

        // Cut within this textblock
        if (count + blockLen >= maxChars) {
            let remaining = maxChars - count
            let foundRelativePos: number | null = null

            // Map character offset to a ProseMirror position inside this textblock
            blockNode.nodesBetween(0, blockNode.content.size, (n: PMNode, p: number) => {
                if (foundRelativePos != null) return false

                if (n.isText) {
                    const t = n.text || ''
                    if (remaining <= t.length) {
                        foundRelativePos = p + remaining
                        return false
                    }
                    remaining -= t.length
                    return true
                }

                // Treat inline atom-ish nodes as a single visible “char”
                if (n.isAtom && n.isInline) {
                    if (remaining <= 1) {
                        foundRelativePos = p
                        return false
                    }
                    remaining -= 1
                }

                return true
            })

            const cutPos =
                foundRelativePos == null
                    ? blockPos + blockNode.nodeSize - 1
                    : blockPos + 1 + foundRelativePos
            const cutBlockEnd = blockPos + blockNode.nodeSize - 1
            return { cutPos, cutBlockEnd }
        }

        count += blockLen

        // Account for newline between blocks
        if (i < blocks.length - 1) {
            count += 1
            if (count >= maxChars) {
                const cutPos = blockPos + blockNode.nodeSize - 1
                const cutBlockEnd = cutPos
                return { cutPos, cutBlockEnd }
            }
        }
    }

    return { cutPos: null, cutBlockEnd: null }
}

function buildMoreWidgetDom(opts: {
    instanceId: string
    buttonId: string
    showMoreControl?: boolean
}): {
    dom: HTMLElement
    destroy: () => void
} {
    const container = document.createElement('span')
    container.className = 'knov-collapse-more-container'
    container.setAttribute('contenteditable', 'false')
    container.setAttribute('tabindex', '-1')

    const ellipsis = document.createElement('span')
    ellipsis.textContent = '... '
    ellipsis.setAttribute('contenteditable', 'false')

    const button = opts.showMoreControl === false ? null : document.createElement('button')
    if (button) {
        button.type = 'button'
        button.textContent = 'more'
        button.className = 'more-button'
        button.setAttribute('data-more-button', 'true')
        button.setAttribute('data-button-id', opts.buttonId)
        button.setAttribute('data-instance-id', opts.instanceId)
        button.setAttribute('contenteditable', 'false')

        // Keep styling consistent with the app-wide `.more-button` styling
        button.style.appearance = 'none'
        button.style.background = 'none'
        button.style.border = '0'
        button.style.padding = '0'
        button.style.margin = '0'
        button.style.font = 'inherit'
        button.style.color = 'rgb(156 147 255)'
        button.style.fontWeight = '600'
        button.style.cursor = 'pointer'
        button.style.display = 'inline-block'
        button.style.userSelect = 'none'
        button.style.outline = 'none'
        // @ts-expect-error - webkitTapHighlightColor is a vendor-prefixed CSS property not in CSSStyleDeclaration
        button.style.webkitTapHighlightColor = 'transparent'
    }

    const stopMoreEvent = (e: Event, opts?: { preventDefault?: boolean }) => {
        if (opts?.preventDefault) e.preventDefault()
        e.stopPropagation()
        ;(e as Event & { stopImmediatePropagation?: () => void }).stopImmediatePropagation?.()
    }

    const dispatchMoreButtonClicked = () => {
        document.dispatchEvent(
            new CustomEvent('more-button-clicked', {
                bubbles: true,
                detail: {
                    buttonId: opts.buttonId,
                    instanceId: opts.instanceId,
                    target: button,
                },
            }),
        )
    }

    const handleAction = (e: Event) => {
        stopMoreEvent(e, { preventDefault: true })

        if (typeof queueMicrotask === 'function') {
            queueMicrotask(dispatchMoreButtonClicked)
        } else {
            Promise.resolve().then(dispatchMoreButtonClicked)
        }
    }

    const handleMouseDown = (e: Event) => stopMoreEvent(e, { preventDefault: true })
    const handlePressStart = (e: Event) => stopMoreEvent(e)

    button?.addEventListener('mousedown', handleMouseDown, true)
    button?.addEventListener('pointerdown', handlePressStart, true)
    button?.addEventListener('click', handleAction, true)
    button?.addEventListener('touchstart', handlePressStart, true)

    container.appendChild(ellipsis)
    if (button) container.appendChild(button)

    return {
        dom: container,
        destroy: () => {
            button?.removeEventListener('mousedown', handleMouseDown, true)
            button?.removeEventListener('pointerdown', handlePressStart, true)
            button?.removeEventListener('click', handleAction, true)
            button?.removeEventListener('touchstart', handlePressStart, true)
        },
    }
}

export const CollapsedContentExtension = Extension.create<CollapsedContentOptions>({
    name: 'knovCollapsedContent',

    addOptions() {
        return {
            enabled: false,
            maxChars: 400,
            maxLines: 10,
            instanceId: 'unknown',
            buttonId: 'expand-button',
            showMoreControl: true,
        }
    },

    addProseMirrorPlugins() {
        const getOptions = () => this.options
        let runtimeOptions = { ...getOptions() }
        const getRuntimeOptions = () => runtimeOptions

        return [
            new Plugin({
                key: collapsedContentPluginKey,
                state: {
                    init: (_config, state) => {
                        const opts = getRuntimeOptions()
                        if (!opts.enabled) return DecorationSet.empty

                        const totalLen = getTotalTextLength(state.doc)
                        const maxLines = opts.maxLines ?? 0
                        const totalLines = getTotalLineCount(state.doc)

                        const exceedsChars = totalLen > opts.maxChars
                        const exceedsLines =
                            Number.isFinite(maxLines) && maxLines > 0 && totalLines > maxLines

                        if (!exceedsChars && !exceedsLines) return DecorationSet.empty

                        const byChars = exceedsChars
                            ? findCutPos(state.doc, opts.maxChars)
                            : { cutPos: null, cutBlockEnd: null }
                        const byLines = exceedsLines
                            ? findCutPosByLines(state.doc, maxLines)
                            : { cutPos: null, cutBlockEnd: null }
                        const { cutPos, cutBlockEnd } = chooseEarlierCut(byChars, byLines)
                        if (!cutPos || !cutBlockEnd) return DecorationSet.empty

                        const decos: Decoration[] = []

                        // Hide remainder of the containing textblock after the cut
                        if (cutPos < cutBlockEnd) {
                            decos.push(
                                Decoration.inline(cutPos, cutBlockEnd, {
                                    class: 'knov-collapse-hidden',
                                }),
                            )
                        }

                        // Hide all nodes that start after the containing block end.
                        // This preserves structural correctness for lists/quotes/etc while hiding the rest.
                        state.doc.descendants((node: PMNode, pos: number) => {
                            if (pos <= cutBlockEnd) return
                            if (!node || node.isText) return
                            decos.push(
                                Decoration.node(pos, pos + node.nodeSize, {
                                    class: 'knov-collapse-hidden',
                                }),
                            )
                        })

                        decos.push(
                            Decoration.widget(
                                cutPos,
                                () => {
                                    const { dom, destroy } = buildMoreWidgetDom({
                                        instanceId: opts.instanceId || 'unknown',
                                        buttonId: opts.buttonId || 'expand-button',
                                        showMoreControl: opts.showMoreControl,
                                    })
                                    // ProseMirror will remove the DOM node; we still need to clean listeners.
                                    ;(
                                        dom as HTMLElement & { __knovDestroy?: () => void }
                                    ).__knovDestroy = destroy
                                    return dom
                                },
                                {
                                    side: 1,
                                },
                            ),
                        )

                        return DecorationSet.create(state.doc, decos)
                    },
                    apply: (tr, prev, _oldState, newState) => {
                        // Recompute on any doc change or when meta indicates option changes.
                        const meta = tr.getMeta(collapsedContentPluginKey)
                        if (meta?.type === 'setOptions') {
                            runtimeOptions = { ...runtimeOptions, ...meta.options }
                        }

                        const opts = getRuntimeOptions()
                        if (!opts.enabled) return DecorationSet.empty

                        const shouldRecompute =
                            tr.docChanged ||
                            meta === 'recompute' ||
                            (meta && ['setVisualCut', 'setOptions'].includes(meta.type))
                        if (!shouldRecompute) {
                            return prev.map(tr.mapping, tr.doc)
                        }

                        const totalLen = getTotalTextLength(newState.doc)
                        const maxLines = opts.maxLines ?? 0
                        const exceedsChars = totalLen > opts.maxChars
                        const visualMeta = meta && meta.type === 'setVisualCut' ? meta : null
                        const exceedsLinesVisual = !!visualMeta?.exceedsLines
                        const exceedsLinesFallback =
                            Number.isFinite(maxLines) &&
                            maxLines > 0 &&
                            getTotalLineCount(newState.doc) > maxLines
                        const exceedsLines = exceedsLinesVisual || exceedsLinesFallback

                        collapseLog(opts.instanceId, 'apply(recompute)', {
                            docChanged: tr.docChanged,
                            metaType: meta?.type || meta,
                            totalLen,
                            maxLines,
                            exceedsChars,
                            exceedsLinesVisual,
                            exceedsLinesFallback,
                            exceedsLines,
                            visualDocLines: visualMeta?.docVisualLines,
                            visualCutPos: visualMeta?.cutPos,
                        })

                        if (!exceedsChars && !exceedsLines) return DecorationSet.empty

                        const byChars = exceedsChars
                            ? findCutPos(newState.doc, opts.maxChars)
                            : { cutPos: null, cutBlockEnd: null }
                        const byLines =
                            exceedsLinesVisual && visualMeta
                                ? { cutPos: visualMeta.cutPos, cutBlockEnd: visualMeta.cutBlockEnd }
                                : exceedsLinesFallback
                                ? findCutPosByLines(newState.doc, maxLines)
                                : { cutPos: null, cutBlockEnd: null }
                        const { cutPos, cutBlockEnd } = chooseEarlierCut(byChars, byLines)
                        if (!cutPos || !cutBlockEnd) return DecorationSet.empty

                        collapseLog(opts.instanceId, 'apply(cut)', {
                            chosen: { cutPos, cutBlockEnd },
                            byChars,
                            byLines,
                        })

                        const decos: Decoration[] = []

                        if (cutPos < cutBlockEnd) {
                            decos.push(
                                Decoration.inline(cutPos, cutBlockEnd, {
                                    class: 'knov-collapse-hidden',
                                }),
                            )
                        }

                        newState.doc.descendants((node: PMNode, pos: number) => {
                            if (pos <= cutBlockEnd) return
                            if (!node || node.isText) return
                            decos.push(
                                Decoration.node(pos, pos + node.nodeSize, {
                                    class: 'knov-collapse-hidden',
                                }),
                            )
                        })

                        decos.push(
                            Decoration.widget(
                                cutPos,
                                () => {
                                    const { dom, destroy } = buildMoreWidgetDom({
                                        instanceId: opts.instanceId || 'unknown',
                                        buttonId: opts.buttonId || 'expand-button',
                                        showMoreControl: opts.showMoreControl,
                                    })
                                    ;(
                                        dom as HTMLElement & { __knovDestroy?: () => void }
                                    ).__knovDestroy = destroy
                                    return dom
                                },
                                { side: 1 },
                            ),
                        )

                        return DecorationSet.create(newState.doc, decos)
                    },
                },
                props: {
                    decorations(state) {
                        return collapsedContentPluginKey.getState(state)
                    },
                    // Ensure our widget cleans up listeners when removed
                    // handleDOMEvents left empty; cleanup happens via MutationObserver below
                    handleDOMEvents: {} as Record<string, never>,
                },
                view: view => {
                    // Visual-line measurement for read-only previews:
                    // We approximate “10 lines” as a max height budget of (line-height * maxLines)
                    // on the ProseMirror root, then cut at the last fully-visible textblock boundary.
                    //
                    // This avoids ugly partial clipping of blockquote/list borders while keeping
                    // “10 lines” meaningful for wrapped lines in narrow stream layouts.
                    let lastVisualCut: {
                        cutPos: number
                        cutBlockEnd: number
                        docVisualLines: number
                        exceedsLines: boolean
                    } | null = null

                    let pendingRAF: number | null = null
                    let isMeasuring = false
                    let retryCount = 0
                    let retryDelayMs = 50
                    let pendingRetryTimeout: ReturnType<typeof setTimeout> | null = null
                    let lastMeasureSignature: string | null = null
                    let lastMeasuredRootSize: string | null = null

                    const optionsSignature = () => {
                        const opts = getRuntimeOptions()
                        return [
                            opts.enabled ? '1' : '0',
                            opts.maxChars,
                            opts.maxLines ?? '',
                            opts.instanceId || '',
                            opts.buttonId || '',
                            opts.showMoreControl === false ? '0' : '1',
                        ].join('|')
                    }

                    const getPmRoot = (): HTMLElement | null => {
                        const viewDom = view.dom as unknown as HTMLElement | null
                        return viewDom && viewDom.classList?.contains('ProseMirror')
                            ? viewDom
                            : (viewDom?.querySelector?.('.ProseMirror') as HTMLElement | null) ??
                                  null
                    }

                    const getRootSizeSignature = () => {
                        const rect = getPmRoot()?.getBoundingClientRect?.()
                        const width = rect?.width
                        const height = rect?.height
                        if (
                            !Number.isFinite(width) ||
                            !Number.isFinite(height) ||
                            width <= 0 ||
                            height <= 0
                        ) {
                            return null
                        }
                        return `${Math.round(width)}x${Math.round(height)}`
                    }

                    const resetRetryWindow = () => {
                        retryCount = 0
                        retryDelayMs = 50
                    }

                    const scheduleMeasure = () => {
                        try {
                            if (pendingRAF != null) cancelAnimationFrame(pendingRAF)
                            pendingRAF = requestAnimationFrame(() => {
                                pendingRAF = null
                                maybeDispatchVisualCut()
                            })
                        } catch (e) {
                            pendingRAF = null
                            maybeDispatchVisualCut()
                        }
                    }

                    const scheduleRetry = (reason: string) => {
                        // Consolidated polling: bounded exponential backoff while layout is unstable.
                        if (retryCount >= 20) return
                        retryCount += 1
                        try {
                            if (pendingRetryTimeout) clearTimeout(pendingRetryTimeout)
                        } catch {}
                        pendingRetryTimeout = setTimeout(() => {
                            pendingRetryTimeout = null
                            collapseLog(
                                getRuntimeOptions().instanceId,
                                `retrying measure (${reason})`,
                                {
                                    attempt: retryCount,
                                    delayMs: retryDelayMs,
                                },
                            )
                            scheduleMeasure()
                        }, retryDelayMs)
                        retryDelayMs = Math.min(500, Math.round(retryDelayMs * 1.6))
                    }

                    function getLineHeightPx(el: HTMLElement): number | null {
                        try {
                            const cs = window.getComputedStyle(el)
                            const lhRaw = cs.lineHeight || ''
                            const lh = parseFloat(lhRaw)
                            if (Number.isFinite(lh) && lh > 0) return lh

                            // Handle "normal" (non-numeric) line-height by measuring.
                            const probe = document.createElement('span')
                            probe.textContent = 'M'
                            probe.style.position = 'absolute'
                            probe.style.visibility = 'hidden'
                            probe.style.pointerEvents = 'none'
                            probe.style.whiteSpace = 'pre'
                            probe.style.font = cs.font
                            probe.style.lineHeight = cs.lineHeight
                            el.appendChild(probe)
                            const measured =
                                probe.getBoundingClientRect().height || probe.offsetHeight || 0
                            el.removeChild(probe)
                            if (measured > 0) return measured

                            const fs = parseFloat(cs.fontSize || '')
                            if (Number.isFinite(fs) && fs > 0) return fs * 1.2
                        } catch (e) {}
                        return null
                    }

                    function computeVisualCut(): {
                        cutPos: number
                        cutBlockEnd: number
                        docVisualLines: number
                        exceedsLines: boolean
                    } | null {
                        try {
                            const opts = getRuntimeOptions()
                            if (!opts.enabled) return null
                            const maxLines = opts.maxLines ?? 0
                            if (!Number.isFinite(maxLines) || maxLines <= 0) return null

                            const blocks = collectTextblocks(view.state.doc)
                            if (!blocks.length) return null

                            const pmRoot = getPmRoot()
                            if (!pmRoot) {
                                collapseLog(
                                    opts.instanceId,
                                    'computeVisualCut(skip): no .ProseMirror root',
                                    {
                                        viewDomClass: (view.dom as HTMLElement | null)?.className,
                                    },
                                )
                                scheduleRetry('no ProseMirror root')
                                return null
                            }

                            // IMPORTANT: measure against the *uncollapsed* layout.
                            // If we measure after applying `.knov-collapse-hidden { display:none }`,
                            // we can oscillate (collapsed -> short -> uncollapse -> tall -> collapse ...).
                            isMeasuring = true
                            pmRoot.classList.add('knov-collapse-measuring')
                            const pmRect = pmRoot.getBoundingClientRect()
                            const pmHeight = Math.max(pmRoot.scrollHeight || 0, pmRect?.height || 0)
                            if (!pmRect || pmHeight <= 0) {
                                collapseLog(
                                    opts.instanceId,
                                    'computeVisualCut(skip): not laid out yet',
                                    {
                                        pmRectH: pmRect?.height,
                                        pmScrollH: pmRoot.scrollHeight,
                                    },
                                )
                                scheduleRetry('pmRoot not laid out')
                                return null
                            }

                            const lh = getLineHeightPx(pmRoot)
                            if (!lh || !Number.isFinite(lh) || lh <= 0) {
                                collapseLog(
                                    opts.instanceId,
                                    'computeVisualCut(skip): invalid line-height',
                                    {
                                        computed: window.getComputedStyle(pmRoot)?.lineHeight,
                                    },
                                )
                                scheduleRetry('invalid line-height')
                                return null
                            }

                            const maxHeight = lh * maxLines
                            const epsilon = 1

                            const docVisualLines = Math.max(1, Math.round(pmHeight / lh))
                            const exceedsLines = pmHeight > maxHeight + epsilon
                            if (!exceedsLines) {
                                collapseLog(
                                    opts.instanceId,
                                    'computeVisualCut(ok): not exceeding',
                                    {
                                        pmHeight,
                                        lh,
                                        maxLines,
                                        docVisualLines,
                                    },
                                )
                                return { cutPos: 0, cutBlockEnd: 0, docVisualLines, exceedsLines }
                            }

                            let lastVisibleIdx = -1
                            let crossingBlock: {
                                index: number
                                node: PMNode
                                pos: number
                                rect: DOMRect
                            } | null = null
                            for (let i = 0; i < blocks.length; i++) {
                                const { node: blockNode, pos: blockPos } = blocks[i]
                                const dom = view.nodeDOM(blockPos) as HTMLElement | null
                                if (!dom || !(dom instanceof HTMLElement)) {
                                    collapseLog(
                                        opts.instanceId,
                                        'computeVisualCut(skip): missing block DOM',
                                        {
                                            i,
                                            blockPos,
                                        },
                                    )
                                    scheduleRetry('missing block DOM')
                                    return null
                                }

                                const r = dom.getBoundingClientRect()
                                if (!r || r.height <= 0) {
                                    collapseLog(
                                        opts.instanceId,
                                        'computeVisualCut(skip): block not laid out',
                                        {
                                            i,
                                            blockPos,
                                            rH: r?.height,
                                        },
                                    )
                                    scheduleRetry('block not laid out')
                                    return null
                                }

                                const bottomRel = r.bottom - pmRect.top
                                if (bottomRel <= maxHeight + epsilon) {
                                    lastVisibleIdx = i
                                    continue
                                }
                                crossingBlock = {
                                    index: i,
                                    node: blockNode,
                                    pos: blockPos,
                                    rect: r,
                                }
                                break
                            }

                            if (crossingBlock) {
                                const crossingBlockEnd =
                                    crossingBlock.pos + crossingBlock.node.nodeSize - 1
                                const crossingTopRel = crossingBlock.rect.top - pmRect.top

                                if (crossingTopRel < maxHeight - epsilon) {
                                    const targetTop = Math.min(
                                        crossingBlock.rect.bottom - 1,
                                        pmRect.top + maxHeight - Math.max(2, lh * 0.35),
                                    )
                                    const targetLeft = Math.max(
                                        crossingBlock.rect.left + 1,
                                        Math.min(crossingBlock.rect.right - 2, pmRect.right - 4),
                                    )
                                    const coords = view.posAtCoords({
                                        left: targetLeft,
                                        top: targetTop,
                                    })
                                    const pos = coords?.pos

                                    if (
                                        pos &&
                                        pos > crossingBlock.pos + 1 &&
                                        pos < crossingBlockEnd
                                    ) {
                                        collapseLog(
                                            opts.instanceId,
                                            'computeVisualCut(ok): crossing block cut',
                                            {
                                                pmHeight,
                                                lh,
                                                maxLines,
                                                docVisualLines,
                                                crossingIdx: crossingBlock.index,
                                                cutPos: pos,
                                            },
                                        )
                                        return {
                                            cutPos: pos,
                                            cutBlockEnd: crossingBlockEnd,
                                            docVisualLines,
                                            exceedsLines,
                                        }
                                    }
                                }
                            }

                            const lastBlock = blocks[Math.max(0, lastVisibleIdx)]
                            const cutPos = lastBlock.pos + lastBlock.node.nodeSize - 1
                            collapseLog(opts.instanceId, 'computeVisualCut(ok): exceeding', {
                                pmHeight,
                                lh,
                                maxLines,
                                docVisualLines,
                                lastVisibleIdx,
                                cutPos,
                            })
                            return {
                                cutPos,
                                cutBlockEnd: cutPos,
                                docVisualLines,
                                exceedsLines,
                            }
                        } catch (e) {
                            try {
                                const opts = getRuntimeOptions()
                                collapseLog(opts.instanceId, 'computeVisualCut(error)', {
                                    error: String(e),
                                })
                            } catch {}
                            return null
                        } finally {
                            try {
                                getPmRoot()?.classList.remove('knov-collapse-measuring')
                            } catch {}
                            isMeasuring = false
                        }
                    }

                    function maybeDispatchVisualCut() {
                        const next = computeVisualCut()
                        if (!next) return

                        // Only dispatch if something actually changed to avoid loops.
                        if (
                            lastVisualCut &&
                            lastVisualCut.cutPos === next.cutPos &&
                            lastVisualCut.cutBlockEnd === next.cutBlockEnd &&
                            lastVisualCut.docVisualLines === next.docVisualLines &&
                            lastVisualCut.exceedsLines === next.exceedsLines
                        ) {
                            return
                        }
                        lastVisualCut = next

                        try {
                            const tr = view.state.tr.setMeta(collapsedContentPluginKey, {
                                type: 'setVisualCut',
                                cutPos: next.cutPos,
                                cutBlockEnd: next.cutBlockEnd,
                                docVisualLines: next.docVisualLines,
                                exceedsLines: next.exceedsLines,
                            })
                            view.dispatch(tr)
                        } catch (e) {}
                    }

                    // Cleanup hook: when PM removes a widget, it doesn't call our destroy.
                    // We attach a MutationObserver that calls __knovDestroy on removed nodes.
                    const observer = new MutationObserver(mutations => {
                        for (const m of mutations) {
                            for (const n of Array.from(m.removedNodes || [])) {
                                const el = n as Node & { __knovDestroy?: () => void }
                                if (el && typeof el.__knovDestroy === 'function') {
                                    try {
                                        el.__knovDestroy()
                                    } catch (e) {}
                                }
                            }
                        }
                    })

                    const shouldMeasureForUpdate = (prevState?: typeof view.state) => {
                        const opts = getRuntimeOptions()
                        const signature = optionsSignature()
                        const rootSize = getRootSizeSignature()
                        const docChanged = !prevState || prevState.doc !== view.state.doc
                        const optionsChanged = signature !== lastMeasureSignature
                        const rootSizeChanged =
                            rootSize != null &&
                            lastMeasuredRootSize != null &&
                            rootSize !== lastMeasuredRootSize
                        const shouldMeasure =
                            !!opts.enabled && (docChanged || optionsChanged || rootSizeChanged)

                        lastMeasureSignature = signature
                        if (rootSize != null) lastMeasuredRootSize = rootSize

                        return shouldMeasure
                    }

                    try {
                        observer.observe(view.dom, { childList: true, subtree: true })
                    } catch (e) {}

                    let resizeObserver: ResizeObserver | null = null
                    try {
                        const pmRoot = getPmRoot()
                        if (pmRoot && typeof ResizeObserver !== 'undefined') {
                            resizeObserver = new ResizeObserver(() => {
                                const opts = getRuntimeOptions()
                                if (!opts.enabled) return
                                if (isMeasuring) return

                                const rootSize = getRootSizeSignature()
                                if (rootSize == null || rootSize === lastMeasuredRootSize) {
                                    return
                                }

                                lastMeasuredRootSize = rootSize
                                resetRetryWindow()
                                scheduleMeasure()
                            })
                            resizeObserver.observe(pmRoot)
                        }
                    } catch (e) {}

                    // Consolidated polling approach:
                    // - Try immediately
                    // - If layout isn't ready (0 height / missing DOM), `scheduleRetry` will re-run.
                    lastMeasureSignature = optionsSignature()
                    lastMeasuredRootSize = getRootSizeSignature()
                    scheduleMeasure()

                    return {
                        update: (_view, prevState) => {
                            // Defer only meaningful changes. Our own visual-cut transaction has
                            // the same doc/options/width; remeasuring it can feed update loops.
                            if (!shouldMeasureForUpdate(prevState)) return

                            resetRetryWindow()
                            scheduleMeasure()
                        },
                        destroy: () => {
                            observer.disconnect()
                            resizeObserver?.disconnect()
                            try {
                                // no-op
                            } catch (e) {}
                            try {
                                if (pendingRAF != null) cancelAnimationFrame(pendingRAF)
                            } catch (e) {}
                            try {
                                if (pendingRetryTimeout) clearTimeout(pendingRetryTimeout)
                            } catch (e) {}
                        },
                    }
                },
            }),
        ]
    },
})
